import type { Translate } from 'next-translate';

import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import {
  isPublicGameWithTeams,
  type PublicGame,
} from '~/domains/contest/domains/services/interfaces/propsService/api/game';

import type {
  PublicPredictionMarket,
  PublicMarketFutureTeamChampion,
  PublicMarketGamelineSpread,
  PublicMarketGamelineTotal,
} from '../services/interfaces/market.types';

import { withMarketLineUnit } from './marketLineUnit';

export type MarketType = 'spread' | 'total' | 'moneyline' | 'future_team_champion' | 'player_prop';
export type TeamType = 'home' | 'away';
export type Side = 'yes' | 'no';

type SelectionTitleParams = {
  market: PublicPredictionMarket;
  marketType: MarketType;
  teamType: TeamType;
  side: Side;
  homeTeam: PublicTeam | undefined;
  awayTeam: PublicTeam | undefined;
  /** Futures markets name their team by `team_id` rather than by home/away. */
  futureTeam: PublicTeam | undefined;
  event: PublicGame | undefined;
  t: Translate;
};

function getEventAliases(
  event: PublicGame | undefined
): { homeAlias: string; awayAlias: string } | null {
  if (!event || !isPublicGameWithTeams(event)) {
    return null;
  }

  return {
    homeAlias: event.home.alias,
    awayAlias: event.away.alias,
  };
}

export function generateFutureSelectionTitle(
  market: PublicMarketFutureTeamChampion,
  side: Side,
  futureTeam: PublicTeam | undefined
): string {
  const lineDisplay = side === 'yes' ? market.yes_line_display : market.no_line_display;
  const futureTeamName = futureTeam?.name ?? futureTeam?.alias;

  return futureTeamName ? `${futureTeamName} - ${lineDisplay}` : lineDisplay;
}

export function generateSelectionTitle({
  market,
  marketType,
  teamType,
  side,
  homeTeam,
  awayTeam,
  futureTeam,
  event,
  t,
}: SelectionTitleParams): string {
  const selectedTeam = teamType === 'home' ? homeTeam : awayTeam;
  const selectedTeamName = selectedTeam?.name ?? selectedTeam?.alias ?? 'Team';
  const eventAliases = getEventAliases(event);
  const homeAlias = homeTeam?.alias ?? eventAliases?.homeAlias ?? 'HOME';
  const awayAlias = awayTeam?.alias ?? eventAliases?.awayAlias ?? 'AWAY';

  switch (marketType) {
    case 'spread': {
      const spreadMarket = market as PublicMarketGamelineSpread;
      const lineDisplay =
        side === 'yes' ? spreadMarket.yes_line_display : spreadMarket.no_line_display;
      return t('selection.spread', {
        team: selectedTeamName,
        line: withMarketLineUnit(lineDisplay, spreadMarket.sub_category, t),
      });
    }

    case 'moneyline': {
      return t('selection.moneyline', { team: selectedTeamName });
    }

    case 'total': {
      const totalMarket = market as PublicMarketGamelineTotal;
      const lineDisplay =
        side === 'yes' ? totalMarket.yes_line_display : totalMarket.no_line_display;
      return t('selection.totalMatchup', {
        away: awayAlias,
        home: homeAlias,
        line: withMarketLineUnit(lineDisplay, totalMarket.sub_category, t),
      });
    }

    case 'future_team_champion': {
      return generateFutureSelectionTitle(
        market as PublicMarketFutureTeamChampion,
        side,
        futureTeam
      );
    }

    default:
      return selectedTeamName;
  }
}

export function generateShortTitle({
  market,
  marketType,
  teamType,
  side,
  homeTeam,
  awayTeam,
  futureTeam,
  event,
}: SelectionTitleParams): string {
  const selectedTeam = teamType === 'home' ? homeTeam : awayTeam;
  const selectedTeamAlias = selectedTeam?.alias ?? 'TM';
  const eventAliases = getEventAliases(event);
  const homeAlias = homeTeam?.alias ?? eventAliases?.homeAlias ?? 'HOME';
  const awayAlias = awayTeam?.alias ?? eventAliases?.awayAlias ?? 'AWAY';

  switch (marketType) {
    case 'spread': {
      const spreadMarket = market as PublicMarketGamelineSpread;
      const lineDisplay =
        side === 'yes' ? spreadMarket.yes_line_display : spreadMarket.no_line_display;
      return `${selectedTeamAlias} ${lineDisplay}`;
    }

    case 'moneyline': {
      return `${selectedTeamAlias} WIN`;
    }

    case 'total': {
      const totalMarket = market as PublicMarketGamelineTotal;
      const lineDisplay =
        side === 'yes' ? totalMarket.yes_line_display : totalMarket.no_line_display;
      return `${awayAlias} @ ${homeAlias} • ${lineDisplay}`;
    }

    case 'future_team_champion': {
      const futureMarket = market as PublicMarketFutureTeamChampion;
      const lineDisplay =
        side === 'yes' ? futureMarket.yes_line_display : futureMarket.no_line_display;
      const futureTeamAlias = futureTeam?.alias ?? futureTeam?.name;

      return futureTeamAlias ? `${futureTeamAlias} - ${lineDisplay}` : lineDisplay;
    }

    default:
      return selectedTeamAlias;
  }
}

export function generateSelectionSubtitle(
  event: PublicGame | undefined,
  homeTeam: PublicTeam | undefined,
  awayTeam: PublicTeam | undefined
): string {
  if (!event) return '';

  const eventAliases = getEventAliases(event);
  const homeAlias = homeTeam?.alias ?? eventAliases?.homeAlias ?? 'HOME';
  const awayAlias = awayTeam?.alias ?? eventAliases?.awayAlias ?? 'AWAY';

  const date = new Date(event.start_date);
  const dateStr = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${awayAlias} @ ${homeAlias} • ${dateStr}`;
}
